<template>
  <AppLayout>
    <div class="flex flex-col items-center gap-4">
      <div class="flex gap-4 w-full max-w-2xl">
        <div class="flex-1">
          <label for="org-select" class="block text-sm font-medium mb-2">Organization</label>
          <Select
            id="org-select"
            v-model="selectedOrganizationId"
            :options="organizationOptions"
            optionLabel="name"
            optionValue="id"
            placeholder="Select Organization"
            class="w-full"
          />
        </div>

        <div class="flex-1">
          <label for="email-filter" class="block text-sm font-medium mb-2">User email</label>
          <InputText
            id="email-filter"
            v-model="emailFilter"
            placeholder="Filter by email..."
            class="w-full"
          />
        </div>
      </div>

      <DataTable
        v-if="users"
        :value="users"
        paginator
        :rows="50"
        :rowsPerPageOptions="[50, 100, 200]"
        tableStyle="min-width: 50rem"
        class="hoverable-rows"
      >
        <Column field="email" header="Email"></Column>
        <Column field="orgName" header="Organization"></Column>
        <Column>
          <template #body="{ data }">
            <div class="flex justify-end">
              <Button
                @click="impersonateUser(data)"
                severity="secondary"
                variant="text"
                label="Impersonate"
                size="small"
              ></Button>
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDebounceFn } from '@vueuse/core';
import type { OrganizationDto, UserDto } from '@/api/generated';
import AppLayout from '@/components/AppLayout.vue';
import { useAuth } from '@/modules/auth/auth';
import { useApiClient } from '@/api/apiClient';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';

const { api } = useApiClient();
const { impersonate } = useAuth();
const route = useRoute();
const router = useRouter();
const users = ref<UserDto[]>([]);
const organizations = ref<OrganizationDto[]>([]);

// Filters live in the URL (?orgId=, ?email=) so a filtered view can be
// deep-linked (e.g. from HQ) and survives refresh/back-nav. The URL is the
// single source of truth: inputs write to it, loading watches it.
function queryString(key: string): string {
  const v = route.query[key];
  return typeof v === 'string' ? v : '';
}

function setQuery(patch: Record<string, string | undefined>) {
  const next: Record<string, string> = { ...(route.query as Record<string, string>) };
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined || v === '') delete next[k];
    else next[k] = v;
  }
  router.replace({ query: next });
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// A hand-edited non-UUID ?orgId= would be rejected by the backend — treat it
// as absent instead of sending it.
const orgIdParam = computed(() => {
  const v = queryString('orgId');
  return UUID_RE.test(v) ? v : '';
});

const selectedOrganizationId = computed({
  get: () => orgIdParam.value,
  set: v => setQuery({ orgId: v || undefined }),
});

// The text input keeps a local ref for debouncing; it writes trimmed to the URL.
const emailFilter = ref(queryString('email'));

const organizationOptions = computed(() => {
  return [
    { id: '', name: 'All organizations' },
    ...organizations.value.map(org => ({ id: org.id, name: org.name })),
  ];
});

let inflightController: AbortController | null = null;

async function loadUsers() {
  inflightController?.abort();
  const controller = new AbortController();
  inflightController = controller;
  try {
    const { data, error } = await api.adminUserControllerList({
      query: {
        organizationId: orgIdParam.value || undefined,
        email: queryString('email') || undefined,
      },
      signal: controller.signal,
    });
    if (controller.signal.aborted) return;
    if (error) {
      throw new Error('Failed to load users');
    }
    users.value = data ?? [];
  } catch (err) {
    if (controller.signal.aborted) return;
    throw err;
  } finally {
    if (inflightController === controller) {
      inflightController = null;
    }
  }
}

async function loadOrganizations() {
  const { data, error } = await api.adminOrganizationControllerList();
  if (error) {
    throw new Error('Failed to load organizations');
  }
  organizations.value = data ?? [];
}

const writeEmailQuery = useDebounceFn(
  (val: string) => setQuery({ email: val.trim() || undefined }),
  300,
);
watch(emailFilter, val => writeEmailQuery(val));

// External URL changes (back/forward nav) → reflect into the input. Compare
// trimmed so the debounced trim-on-write doesn't echo into what the user is
// still typing.
watch(
  () => queryString('email'),
  val => {
    if (val !== emailFilter.value.trim()) emailFilter.value = val;
  },
);

// One load per URL state — covers the org select, typed email (already
// debounced via the URL write) and history navigation alike.
watch(
  () => [orgIdParam.value, queryString('email')],
  () => loadUsers(),
);

loadUsers();
loadOrganizations();

function impersonateUser(user: UserDto) {
  impersonate(user.id);
}
</script>

<style scoped>
.hoverable-rows :deep(tbody tr) {
  transition: background-color 0.2s ease;
  cursor: pointer;
}

.hoverable-rows :deep(tbody tr:hover) {
  background-color: #f9fafb;
}
</style>
